import { trpcClient } from "@/trpc/client";
import { useQuery } from "@tanstack/react-query";

/**
 * How often we re-fetch the business doc to pick up an admin flipping the
 * idle-logout switch. The feature operates on 10-minute/60-second scales, so
 * this only needs to be "well under a minute", not real-time — a dedicated
 * poll (rather than the app-wide business query's 2-minute staleTime) is
 * what makes the setting "take effect without a reload" per GRA-80.
 */
const POLL_INTERVAL_MS = 30_000;

/**
 * Live value of `Businesses/{businessId}.security.idleLogoutMinutes`.
 * Returns `null` while loading or when idle logout is off.
 */
export function useIdleLogoutMinutes(
  businessId: string | null | undefined,
): number | null {
  const { data } = useQuery({
    queryKey: ["idle-logout-setting", businessId],
    queryFn: () => trpcClient.business.get.query({ id: businessId as string }),
    enabled: !!businessId,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
    select: (business) =>
      (business as { security?: { idleLogoutMinutes: number | null } } | null)
        ?.security?.idleLogoutMinutes ?? null,
  });
  return data ?? null;
}
